import {observable, action, computed, ObservableMap, makeObservable, observe} from 'mobx';
import {SurveyRequester} from "../MobxStores/RequestApi/SurveyRequester"
import {SurveyQuestionTypeEnum} from "../serverClientShared/sharedEnums";
import {routes} from "../App";
import {history} from "../index";

export class SurveyStore {


    currentSurvey = {}
    currentQuestions = []
    isModal = true
    isSurveySubmitted = false
    currentLanguage = 'EN-US'


    constructor(modalStore, generalStore) {
        makeObservable(this, {
            currentSurvey: observable,
            currentQuestions: observable,
            isModal: observable,
            isSurveySubmitted: observable,
            currentLanguage: observable,
            validateSurveyAnswers : computed,
            skipSurvey: action,
            checkIfPermissionOrAnswerAlreadyExists: action,
            getSurvey: action,
            submitSurvey: action,
        });
        this.modalStore = modalStore;
        this.generalStore = generalStore;

    }

    // Question Management API Methods
    listQuestions = async (surveyType) => {
        const db = this.generalStore.workspace;
        if (!db) throw new Error("Workspace not available");
        return await SurveyRequester.listQuestions(db, surveyType);
    }

    createQuestion = async (surveyType, questionText, questionType, options = []) => {
        const db = this.generalStore.workspace;
        if (!db) throw new Error("Workspace not available");
        return await SurveyRequester.createQuestion(db, surveyType, questionText, questionType, options);
    }

    updateQuestion = async (surveyType, questionId, data) => {
        const db = this.generalStore.workspace;
        if (!db) throw new Error("Workspace not available");
        return await SurveyRequester.updateQuestion(db, surveyType, questionId, data);
    }

    deleteQuestion = async (surveyType, questionId) => {
        const db = this.generalStore.workspace;
        if (!db) throw new Error("Workspace not available");
        return await SurveyRequester.deleteQuestion(db, surveyType, questionId);
    }

    reorderQuestions = async (surveyType, orderedQuestionIds) => {
        const db = this.generalStore.workspace;
        if (!db) throw new Error("Workspace not available");
        return await SurveyRequester.reorderQuestions(db, surveyType, orderedQuestionIds);
    }

    getAnalytics = async (surveyType, dateRange) => {
        const db = this.generalStore.workspace;
        if (!db) throw new Error("Workspace not available");
        return await SurveyRequester.getAnalytics(db, surveyType, dateRange);
    }

    getTextAnswerPagination = async (surveyType, questionId, dateRange, skip, limit) => {
        const db = this.generalStore.workspace;
        if (!db) throw new Error("Workspace not available");
        return await SurveyRequester.getTextAnswerPagination(db, surveyType, questionId, dateRange, skip, limit);
    }

    skipSurvey = async () => {
        this.modalStore.isSurveyPopupOpen = false
        this.modalStore.isSurveyOpen = false
        if (this.currentSurvey.surveyType && this.generalStore.workspace && this.currentSurvey.surveyIdentifier){
            SurveyRequester.submitAnswer(this.generalStore.workspace, this.currentSurvey.surveyType, {isSkipped: true, surveyIdentifier: this.currentSurvey.surveyIdentifier}).then()
        }
    }
    checkIfPermissionOrAnswerAlreadyExists = async () => {
        this.modalStore.isSurveyPopupOpen = false
        if (this.currentSurvey.surveyType && this.generalStore.workspace && this.currentSurvey.surveyIdentifier){
            const result =  await SurveyRequester.checkAnswerExists(this.generalStore.workspace, this.currentSurvey.surveyType, {isSkipped: true, surveyIdentifier: this.currentSurvey.surveyIdentifier}).then()
            if (result.status === 200) {
                const {alreadyAnswered,permission, isSkipped} = result.response
                if (!permission && !this.isModal){
                    history.push(routes.NOT_FOUND)
                }
                if (alreadyAnswered){
                        if (!this.isModal && isSkipped){
                            return false
                        } else if (!this.isModal){
                            this.isSurveySubmitted = true
                        }
                }
                return alreadyAnswered
            } else if (!this.isModal){
                history.push(routes.NOT_FOUND)
            }
        }
        return true
    }
    getSurvey = async () =>{
        if (this.currentSurvey.surveyType && this.generalStore.workspace && this.currentSurvey.surveyIdentifier){
            const result =  await SurveyRequester.listQuestions(this.generalStore.workspace, this.currentSurvey.surveyType, this.currentSurvey.surveyIdentifier)
            if (result.status === 200) {
                this.currentQuestions = result.questions
            }
        }
    }
    submitSurvey = async () => {
        this.generalStore.isLoaderOpen = true;
        try {
            if (!this.currentSurvey.surveyType || !this.generalStore.workspace || !this.currentSurvey.surveyIdentifier || !this.validateSurveyAnswers) {
                return;
            }
            this.currentQuestions = this.currentQuestions.map(question => {
                if (!this.validateAnswerByType(question)) {
                    question.IsSkipped = true;
                    delete question.SelectedOption;
                    delete question.RatingValue;
                    delete question.BooleanValue;
                    delete question.TextValue;
                }
                return question;
            });
            const result = await SurveyRequester.submitAnswer(
                this.generalStore.workspace,
                this.currentSurvey.surveyType,
                {
                    isSkipped: false,
                    answersArray: this.currentQuestions,
                    surveyIdentifier: this.currentSurvey.surveyIdentifier,
                    language: this.currentLanguage,
                },
            );
            if (result.status === 200) {
                this.isSurveySubmitted = true;
                this.modalStore.isSurveyOpen = false;
                return result.response;
            }
        } catch (error) {
            console.error('Failed to submit survey:', error);
            this.modalStore.showErrorMessage(this.generalStore.translate('failedToSubmitSurvey'));
        } finally {
            this.generalStore.isLoaderOpen = false;
        }
    }
    get validateSurveyAnswers (){
        return this.currentQuestions.some(question => this.validateAnswerByType(question))
    }

     validateAnswerByType = (question) => {
        if (!question) return false
        switch (question.QuestionType) {
            case SurveyQuestionTypeEnum.RATING_SCALE: {
                const validRating = question.RatingValue !== undefined && question.RatingValue >= 1 && question.RatingValue<= 5;
                return validRating;
            }
            case SurveyQuestionTypeEnum.MULTIPLE_CHOICE: {
                const validOption = question.SelectedOption !== undefined && question.Options.some(opt => opt.OptionId === question.SelectedOption)
                return validOption;
            }
            case SurveyQuestionTypeEnum.YES_NO :{
                const validOption = typeof question.BooleanValue === 'boolean' && [true, false].includes(question.BooleanValue);
                return validOption;
            }
            case SurveyQuestionTypeEnum.TEXT_RESPONSE : {
                const validText = typeof question.TextValue === 'string' && question.TextValue.trim().length > 0;
                return validText;
            }
            default:
                return false;
        }
    }


}